/**
 * Flux temps réel des traitements en arrière-plan (SSE), partagé par tout
 * l'onglet : une seule connexion, ouverte tant qu'un écran s'en sert (menu,
 * page du lot, revue, modales, écran des traitements), refermée sinon.
 *
 * Au (re)branchement, le serveur renvoie l'état courant lu en base : rien
 * n'est perdu pendant une coupure. Coupure réseau : le navigateur se
 * rebranche seul (`retry` du serveur) ; refus (session expirée, serveur
 * arrêté) : nouvel essai, de plus en plus espacé (jusqu'à 30 s).
 *
 * Une opération qui ne bat plus depuis 2 min est lue « interrompue » sur
 * l'horloge locale (serveur arrêté : aucun message ne le dira).
 */

/** Opération telle que le flux la décrit (ligne d'historique). */
export interface BulkRun {
  id: number
  /** Lot de l'opération ; `null` pour un aperçu. */
  batchId: number | null
  /** Aperçu d'un fichier (opération `preview`), sinon `null`. */
  previewId: number | null
  operation: DraftBatchOperation['operation'] | 'preview'
  status: 'running' | 'done' | 'failed' | 'interrupted'
  interrupted: boolean
  byName: string
  startedAt: string
  finishedAt: string | null
  heartbeatAt: string | null
  total: number
  done: number
  error: string | null
  relaunchedById: number | null
}

export type OperationsStreamEvent = { type: 'run', run: BulkRun } | { type: 'snapshot', running: BulkRun[], attention: number }

/** Sans battement depuis ce délai : interrompue (règle du serveur). */
export const BULK_STALE_MS = 2 * 60_000

const state = reactive({
  connected: false,
  /** Un état complet a été reçu depuis le dernier branchement. */
  ready: false,
  running: {} as Record<number, BulkRun>,
  attention: 0,
  now: Date.now(),
})
const listeners = new Set<(e: OperationsStreamEvent) => void>()
let source: EventSource | null = null
let users = 0
let retry = 0
let retryTimer: ReturnType<typeof setTimeout> | null = null
let clock: ReturnType<typeof setInterval> | null = null

/** Interrompue : marquée par le serveur, ou sans battement depuis 2 min. */
export function isRunInterrupted(run: BulkRun, now = state.now): boolean {
  if (run.interrupted || run.status === 'interrupted') return true
  if (run.status !== 'running') return false
  const beat = Date.parse(run.heartbeatAt ?? run.startedAt)
  return now - beat > BULK_STALE_MS
}

/** Vue « opération du lot » attendue par les badges et les modales. */
export function runAsOperation(run: BulkRun): DraftBatchOperation {
  // Opérations d'un lot seulement (un aperçu n'a pas de lot).
  return { operation: run.operation as DraftBatchOperation['operation'], startedAt: run.startedAt, byName: run.byName, total: run.total, done: run.done, interrupted: isRunInterrupted(run) }
}

function handle(e: OperationsStreamEvent) {
  if (e.type === 'snapshot') {
    state.running = Object.fromEntries(e.running.map(r => [r.id, r]))
    state.attention = e.attention
    state.ready = true
  }
  else if (e.run.status === 'running') {
    state.running = { ...state.running, [e.run.id]: e.run }
  }
  else if (state.running[e.run.id]) {
    const { [e.run.id]: _fini, ...rest } = state.running
    state.running = rest
  }
  for (const l of listeners) l(e)
}

function connect() {
  if (source || !import.meta.client) return
  source = new EventSource(api.operations.events())
  source.onopen = () => {
    retry = 0
    state.connected = true
  }
  source.onmessage = (msg) => {
    try {
      handle(JSON.parse(msg.data) as OperationsStreamEvent)
    }
    catch { /* message illisible : ignoré */ }
  }
  source.onerror = () => {
    state.connected = false
    state.ready = false
    // Le navigateur se rebranche seul tant que la connexion n'est pas close.
    if (source?.readyState !== EventSource.CLOSED) return
    source.close()
    source = null
    if (users > 0) {
      retryTimer = setTimeout(() => {
        retryTimer = null
        connect()
      }, Math.min(1000 * 2 ** retry++, 30_000))
    }
  }
  clock ??= setInterval(() => { state.now = Date.now() }, 15_000)
}

function disconnect() {
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
  source?.close()
  source = null
  if (clock) clearInterval(clock)
  clock = null
  state.connected = false
  state.ready = false
}

/** Écoute les messages du flux ; rend la fonction qui arrête l'écoute. */
export function onOperationsEvent(fn: (e: OperationsStreamEvent) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Ouvre le flux (s'il ne l'est pas) ; rend la fonction qui le libère. */
export function acquireOperationsStream(): () => void {
  users++
  connect()
  let released = false
  return () => {
    if (released) return
    released = true
    users--
    if (users <= 0) {
      users = 0
      disconnect()
    }
  }
}

/**
 * Flux des traitements pour un composant : branché au montage, libéré au
 * démontage. `onEvent` : appelé à chaque message, le temps du composant.
 */
export function useOperationsStream(onEvent?: (e: OperationsStreamEvent) => void) {
  if (import.meta.client) {
    let release: (() => void) | null = null
    let stop: (() => void) | null = null
    onMounted(() => {
      release = acquireOperationsStream()
      if (onEvent) stop = onOperationsEvent(onEvent)
    })
    onBeforeUnmount(() => {
      stop?.()
      release?.()
    })
  }

  const runs = computed(() => Object.values(state.running))
  /** En cours réellement (hors interrompues). */
  const runningCount = computed(() => runs.value.filter(r => !isRunInterrupted(r, state.now)).length)
  /** Échecs et interruptions non relancés. */
  const attentionCount = computed(() => state.attention + runs.value.filter(r => r.relaunchedById == null && isRunInterrupted(r, state.now) && !r.interrupted).length)

  /** Aperçu en calcul (un seul à la fois), ou `null`. */
  const runningPreview = computed(() => runs.value.find(r => r.operation === 'preview' && !isRunInterrupted(r, state.now)) ?? null)

  /** Opération en cours sur un lot (la plus récente), ou `null`. */
  function operationOf(batchId: number): DraftBatchOperation | null {
    const run = runs.value.filter(r => r.batchId === batchId).sort((a, b) => b.id - a.id)[0]
    return run ? runAsOperation(run) : null
  }

  return { state: readonly(state), runs, runningCount, attentionCount, operationOf, runningPreview }
}

/**
 * Attend la fin d'une opération lancée en arrière-plan (202) sur un lot, par
 * le flux : l'avancement part à `onTick`, l'issue est relue sur le lot
 * (`operation`) dès que le flux annonce une fin — ou un état où l'opération
 * n'est plus. `null` si elle ne répond plus (interrompue).
 */
export async function waitForDraftOperation<R>(
  batchId: number,
  onTick: (o: DraftBatchOperation | null) => void = () => {},
): Promise<DraftOperationResult<R> | null> {
  const release = acquireOperationsStream()
  try {
    return await new Promise<DraftOperationResult<R> | null>((resolve) => {
      let last: BulkRun | null = null
      let checking = false
      let settled = false
      const finish = (value: DraftOperationResult<R> | null) => {
        if (settled) return
        settled = true
        stop()
        clearInterval(watchdog)
        resolve(value)
      }
      /** Relit l'état du lot : fin (issue), interruption, ou toujours en cours. */
      const check = async () => {
        if (checking || settled) return
        checking = true
        try {
          const res = await $fetch<{ data: { operation: DraftBatchOperation | null, result: DraftOperationResult<R> | null } }>(api.drafts.operation(batchId))
          const { operation, result } = res.data
          onTick(operation)
          if (operation?.interrupted) finish(null)
          else if (!operation) finish(result)
        }
        catch { /* prochain message du flux */ }
        finally {
          checking = false
        }
      }
      const stop = onOperationsEvent((e) => {
        if (e.type === 'snapshot') {
          last = e.running.filter(r => r.batchId === batchId).sort((a, b) => b.id - a.id)[0] ?? null
          void check()
        }
        else if (e.run.batchId === batchId) {
          last = e.run
          if (e.run.status === 'running') onTick(runAsOperation(e.run))
          else void check()
        }
      })
      // Serveur arrêté en route : plus aucun message. L'horloge locale juge
      // l'opération interrompue après 2 min sans battement.
      const watchdog = setInterval(() => {
        if (last && isRunInterrupted(last, Date.now())) void check()
      }, 15_000)
      void check()
    })
  }
  finally {
    release()
  }
}

/**
 * Opération en cours sur un lot, vue en temps réel : celle du flux si elle y
 * figure, sinon celle lue avec l'écran (`initial`) — jusqu'à ce que le flux
 * en annonce la fin. `onFinished` : une opération du lot vient de finir
 * (l'écran relit le lot).
 */
export function useBatchOperationLive(
  batchId: MaybeRefOrGetter<number>,
  initial: MaybeRefOrGetter<DraftBatchOperation | null | undefined>,
  onFinished: () => void,
) {
  const ended = ref(false)
  let seen = false
  const { operationOf } = useOperationsStream((e) => {
    const id = toValue(batchId)
    if (e.type === 'run' && e.run.batchId === id) {
      if (e.run.status === 'running') {
        seen = true
        return
      }
      ended.value = true
      seen = false
      onFinished()
    }
    else if (e.type === 'snapshot') {
      const here = e.running.some(r => r.batchId === id)
      // Fin survenue pendant une coupure : l'état neuf ne la porte plus.
      if (seen && !here) {
        ended.value = true
        onFinished()
      }
      seen = here
    }
  })
  watch(() => toValue(initial), () => { ended.value = false })
  return computed<DraftBatchOperation | null>(() => operationOf(toValue(batchId)) ?? (ended.value ? null : toValue(initial) ?? null))
}
